import { useRouteContext } from "@tanstack/react-router";
import type { JSX } from "react";

import Document from "@repo/icons/default/Document";

import { type FileObjectType, useFileSelector } from "@/hooks/use-file-selector";
import { useLivanceAPIMutation } from "@/hooks/use-livance-api";
import { formatWithZonedDate } from "@/lib/helpers/date";

interface UsuarioArquivoResponse {
  url: string;
  codUsuarioArquivo: number;
}

interface FileUploadCardProps {
  title?: string;
  description?: string;
  codTipoArquivo: number;
  onUploadFile?: (fileId: string) => void;
  onError?: () => void;
  allowPdf?: boolean;
}

export const FileUploadCard = ({
  title = "Anexar documento",
  description,
  codTipoArquivo,
  onUploadFile,
  onError,
  allowPdf = false,
}: FileUploadCardProps): JSX.Element => {
  const { user } = useRouteContext({ from: "__root__" });

  const uploadMutation = useLivanceAPIMutation<UsuarioArquivoResponse>("UsuarioArquivo", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });

  const handleUpload = async (file: FileObjectType): Promise<void> => {
    if (!file.file || file.file.byteLength === 0) {
      onError?.();
      return;
    }

    const body = {
      arquivo: Array.from(file.file),
      codUsuario: user.codUsuario,
      dataCadastro: formatWithZonedDate(new Date(), "yyyy-MM-dd"),
      mimetype: file.mimetype,
      codTipoArquivo,
    };

    await uploadMutation.mutateAsync(body, {
      onSuccess: (data) => {
        onUploadFile?.(data.codUsuarioArquivo.toString());
      },
      onError,
    });
  };

  const { openFileSelector, dataUrl, isUploading, error } = useFileSelector({
    onUploadFile: handleUpload,
    onError,
    allowPdf,
  });

  const isLoading = isUploading || uploadMutation.isPending;
  const defaultDescription = allowPdf
    ? "Carregue o seu certificado em JPG, PNG ou PDF"
    : "Carregue o seu certificado em JPG ou PNG";
  const resolvedDescription = description ?? defaultDescription;

  return (
    <button
      type="button"
      onClick={() => {
        void openFileSelector();
      }}
      disabled={isLoading}
      className="flex flex-col bg-neutral-50 border border-neutral-50 rounded-lg px-4 py-4 text-left w-full disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <div className="flex flex-row gap-2 justify-between items-center">
        <div className="flex flex-col">
          <span className="text-base font-semibold text-neutral-600">
            {isLoading ? "Carregando..." : title}
          </span>
          <span className="text-xs text-neutral-500">
            {dataUrl ? "1 documento anexado" : resolvedDescription}
          </span>
        </div>
        {dataUrl ? <Document size={24} /> : null}
      </div>

      {error ? <span className="text-xs text-danger-500 mt-1">{error}</span> : null}
    </button>
  );
};
